import { useCallback, useEffect, useRef, useState } from "react";

import noop from "lodash/noop";

import {
  MessagesAnalyticsEvent,
  MessagesAnalyticsKey,
} from "@/constants/analytics/Analytics";
import type {
  GroupChannel,
  SendbirdInputProps,
} from "@/crossPlatform/Sendbird";
import { createGroupChannelFragment } from "@/crossPlatform/Sendbird";
import { useAnalytics } from "@/services/Analytics";
import { useAppUserSelector } from "@/store/reducers/appUser";
import ChannelHeader from "@/ui/components/messages/ChannelHeader";
import ChannelInput from "@/ui/components/messages/fragments/ChannelInput";
import GroupChannelFileMessage from "@/ui/components/messages/fragments/GroupChannelFileMessage";
import GroupChannelMessage from "@/ui/components/messages/fragments/GroupChannelMessage";
import { Spinner } from "@/ui/core";
import { Box } from "@/ui/layout";
import { WebContentMaxWidth } from "@/ui/layout/ScreenContainer";
import { isIndividualChannel } from "@/utils/groupChannel.util";
import { sendbirdMemberToUserAvatar } from "@/utils/messages.util";
import { isWeb } from "@/utils/platform.util";

interface GroupChannelFragmentProps {
  channel: GroupChannel;
  channelDisplayName: string;
  isUserInPostTreatment: boolean;
  onDetailsPress: () => void;
  onDismiss: () => void;
}

// on web, only show our spinner if the delay is notable, otherwise a spinner flicker on every channel press
const SpinnerDelayMs = 100;

const DelayedSpinner = () => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const timeoutId = setTimeout(() => setIsVisible(true), SpinnerDelayMs);
    return () => clearTimeout(timeoutId);
  }, []);

  if (!isVisible) return null;

  return (
    <Box alignItems="center" flex={1} justifyContent="center">
      <Spinner size="large" />
    </Box>
  );
};

// kept at module-scope to ensure stable identities and to ensure no unintentional re-renders triggered
const renderWebStatusLoading = () => <DelayedSpinner />;
// kept at module-scope to ensure stable identities and to ensure no unintentional re-renders triggered
const renderNativeStatusLoading = () => null;

const GroupChannelFragment = ({
  channel,
  channelDisplayName,
  isUserInPostTreatment,
  onDetailsPress,
  onDismiss,
}: GroupChannelFragmentProps) => {
  const { externalId: appUserId } = useAppUserSelector();
  const isGroupChannel = !isIndividualChannel(channel.url);
  const { track } = useAnalytics();

  const handleOnDetailsPress = useCallback(() => {
    track(MessagesAnalyticsEvent.AboutMessagesPressed, {
      [MessagesAnalyticsKey.ChannelName]: channelDisplayName,
      [MessagesAnalyticsKey.IsGroupChannel]: isGroupChannel,
    });

    onDetailsPress();
  }, [track, channelDisplayName, isGroupChannel, onDetailsPress]);

  const renderHeader = useCallback(
    () => (
      <ChannelHeader
        avatarUserId={
          isWeb() && !isGroupChannel
            ? channel.members.find((m) => m.userId !== appUserId)?.userId
            : undefined
        }
        channelName={channelDisplayName || channel.name}
        channelUrl={channel.url}
        isGroupChannel={isGroupChannel}
        members={channel.members.map(sendbirdMemberToUserAvatar)}
        onDetailsPress={handleOnDetailsPress}
        onDismiss={onDismiss}
      />
    ),
    [
      appUserId,
      channelDisplayName,
      channel.name,
      channel.url,
      isGroupChannel,
      channel.members,
      handleOnDetailsPress,
      onDismiss,
    ],
  );

  const renderInput = useCallback(
    ({
      onPressSendFileMessage,
      onPressSendUserMessage,
    }: SendbirdInputProps) => (
      <ChannelInput
        channel={channel}
        channelDisplayName={channelDisplayName}
        isChannelFrozen={channel.isFrozen || isUserInPostTreatment}
        isGroupChannel={isGroupChannel}
        members={channel.members.map(sendbirdMemberToUserAvatar)}
        onPressSendFileMessage={onPressSendFileMessage}
        onPressSendUserMessage={onPressSendUserMessage}
      />
    ),
    [channel, channelDisplayName, isUserInPostTreatment, isGroupChannel],
  );

  const partsRef = useRef({ renderHeader, renderInput });
  partsRef.current = { renderHeader, renderInput };

  // createGroupChannelFragment returns a NEW component function on every invocation (here, ie: every render)
  // this results in the fragment being un-mounted + re-mounted fully, which on web triggers re-fetching on every render
  // (and ultimately ~30s loads). so a ref is used to ensure a stable identity that is instantiated only once
  const createdFragmentRef = useRef<null | ReturnType<
    typeof createGroupChannelFragment
  >>(null);
  // useMemo seems tempting but is not guaranteed to run only once even with empty [] dependencies
  // See: https://react.dev/reference/react/useMemo#caveats
  //    However, since useMemo is performance optimization, not a semantic guarantee, React may throw away the cached value if there is a specific reason to do that.
  if (createdFragmentRef.current == null) {
    createdFragmentRef.current = createGroupChannelFragment({
      // partsRef.current inside this function ensures an up-to-date/never-stale renderHeader()
      Header: () => partsRef.current.renderHeader(),
      // partsRef.current inside this function ensures an up-to-date/never-stale renderInput()
      Input: (props: SendbirdInputProps) => partsRef.current.renderInput(props),
      StatusLoading: isWeb()
        ? renderWebStatusLoading
        : renderNativeStatusLoading,
    });
  }
  const CreatedFragment = createdFragmentRef.current;

  const onPressHeaderLeft = isWeb() ? onDismiss : noop;
  const [alignSelf, flex, maxWidth, width] = isWeb()
    ? ["center" as const, 1, WebContentMaxWidth, "100%"]
    : [undefined, 1, undefined, undefined];

  return (
    <Box alignSelf={alignSelf} flex={flex} maxWidth={maxWidth} width={width}>
      <CreatedFragment
        channel={channel}
        onChannelDeleted={noop}
        onPressHeaderLeft={onPressHeaderLeft}
        onPressHeaderRight={noop}
        renderMessage={(props: Parameters<typeof GroupChannelMessage>[0]) => {
          if (props.message.isFileMessage()) {
            const fileMessage = props.message;
            return (
              <GroupChannelFileMessage
                channel={channel}
                message={fileMessage}
              />
            );
          }

          // eslint-disable-next-line react/jsx-props-no-spreading
          return <GroupChannelMessage {...props} />;
        }}
      />
    </Box>
  );
};

export default GroupChannelFragment;
